import React, { useState } from 'react'
import { IonModal } from '@ionic/react'

import ButtonComponent from '../basic-button/ButtonComponent'

import './LegalConsentsModal.scss'

interface LegalConsentsPayload {
  marketingOwnAccepted: boolean
  marketingThirdPartyAccepted: boolean
  siiAccessAuthorizationAccepted: boolean
}

interface Props {
  isOpen: boolean
  onAccept: (payload: LegalConsentsPayload) => Promise<void> | void
}

/**
 * Modal bloqueante para usuarios chilenos ya registrados que aún no aceptaron
 * los nuevos consentimientos legales. Se dispara cuando el flag
 * `legalConsentsRequiredCl` está activo en la colección `apps` y el usuario
 * no tiene los 3 consentimientos en `true`.
 */
const LegalConsentsModal: React.FC<Props> = ({ isOpen, onAccept }) => {
  const [marketingOwn, setMarketingOwn] = useState(false)
  const [marketingThirdParty, setMarketingThirdParty] = useState(false)
  const [siiAuth, setSiiAuth] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  const handleContinue = async () => {
    if (submitting) return
    try {
      setSubmitting(true)
      setErrorMessage(null)
      // Consentimientos OPCIONALES: se guarda lo que el usuario efectivamente
      // marcó (no se fuerza a true). Puede continuar sin marcar ninguno.
      await onAccept({
        marketingOwnAccepted: marketingOwn,
        marketingThirdPartyAccepted: marketingThirdParty,
        siiAccessAuthorizationAccepted: siiAuth,
      })
    } catch (err) {
      setErrorMessage('No pudimos guardar tu respuesta. Revisá tu conexión y reintentá.')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <IonModal className="legal-consents-modal" isOpen={isOpen} backdropDismiss={false}>
      <div className="legal-consents-wrapper">
        <h3 className="legal-consents-title">Autorizaciones</h3>
        <p className="legal-consents-subtitle">
          Estas autorizaciones son opcionales. Marca las que quieras activar, puedes continuar sin seleccionar ninguna.
        </p>

        <div className="legal-consents-list">
          <div className="legal-consent-row" onClick={() => setMarketingOwn(!marketingOwn)}>
            <div className={`checkbox ${marketingOwn ? 'checked' : ''}`}></div>
            <span>
              Autorizo a Almacén Gurú SpA a enviarme comunicaciones promocionales de sus productos y servicios.
            </span>
          </div>

          <div className="legal-consent-row" onClick={() => setMarketingThirdParty(!marketingThirdParty)}>
            <div className={`checkbox ${marketingThirdParty ? 'checked' : ''}`}></div>
            <span>
              Quiero recibir promociones de productos para mi negocio proveídos por terceros, comunicados por Almacén Gurú o el proveedor de dicho producto.
            </span>
          </div>

          <div className="legal-consent-row" onClick={() => setSiiAuth(!siiAuth)}>
            <div className={`checkbox ${siiAuth ? 'checked' : ''}`}></div>
            <span>
              Autorizo a Almacén Gurú SpA a acceder a mi información de compra y datos de mi negocio, mediante certificado digital, para procesarlos y entregarme análisis de mi negocio.
            </span>
          </div>
        </div>

        {errorMessage && (
          <div className="legal-consents-error" role="alert">
            {errorMessage}
          </div>
        )}

        <div className="legal-consents-actions">
          <ButtonComponent
            className={`btn-primary ${submitting ? 'loading' : ''}`}
            disabled={submitting}
            onClick={handleContinue}
            text={submitting ? 'Guardando...' : (errorMessage ? 'Reintentar' : 'Continuar')}
          />
        </div>
      </div>
    </IonModal>
  )
}

export default LegalConsentsModal
